// Pizzapur Legal Documents — Last Updated June 2025

export const LEGAL_DOCS = {
  'terms': {
    title: 'Terms & Conditions',
    lastUpdated: 'June 2025',
    sections: [
      {
        heading: 'Acceptance of Terms',
        body:
          "By accessing and using www.pizzapur.com (the 'Website'), you agree to be bound by these Terms and Conditions. If you do not agree with any part of these terms, please do not use this Website. These terms apply to all visitors, users, and others who access or use the Website.",
      },
      {
        heading: 'About the Website',
        body:
          'Pizzapur is a vegetarian quick-service pizza brand based in Jodhpur, Rajasthan, India. This Website is an informational platform providing details about the Pizzapur brand, menu, and franchise opportunities. The Website does not currently facilitate online food orders or direct purchases.',
      },
      {
        heading: 'Intellectual Property',
        body:
          'All content on this Website — including but not limited to the Pizzapur name, logo, brand identity, images, text, graphics, videos, recipes, and SOP materials — is the exclusive intellectual property of Pizzapur. Unauthorized use, reproduction, or distribution of any content is strictly prohibited. You may not copy, reproduce, or distribute any content without prior written permission from Pizzapur. The Pizzapur name, logo, and brand assets are registered trademarks and may not be used without authorization. Any unauthorized use will be subject to legal action under applicable Indian laws.',
      },
      {
        heading: 'Franchise Inquiries',
        body:
          'Information provided on this Website regarding franchise opportunities is for general reference only. It does not constitute a legally binding offer or agreement. Actual franchise terms will be governed by the signed Franchise Agreement between Pizzapur and the franchisee. Pizzapur reserves the right to modify franchise terms at any time.',
      },
      {
        heading: 'Accuracy of Information',
        body:
          'Pizzapur makes every effort to ensure that the information on this Website is accurate and up to date. However, we do not warrant that all information is complete, accurate, or current at all times. We reserve the right to modify, update, or remove any content without prior notice.',
      },
      {
        heading: 'Third-Party Links',
        body:
          'This Website may contain links to third-party websites (such as Zomato, Swiggy, or social media platforms). Pizzapur is not responsible for the content, policies, or practices of any third-party websites. Accessing third-party links is at your own risk.',
      },
      {
        heading: 'Limitation of Liability',
        body:
          'To the fullest extent permitted by law, Pizzapur shall not be liable for any direct, indirect, incidental, or consequential damages arising from:',
        bullets: [
          'Your use of or inability to use this Website',
          'Any errors or omissions in the Website content',
          'Any unauthorized access to your data or information',
          'Any interruptions or unavailability of the Website',
        ],
      },
      {
        heading: 'Governing Law & Jurisdiction',
        body:
          'These Terms and Conditions shall be governed by and construed in accordance with the laws of India. Any disputes arising from the use of this Website shall be subject to the exclusive jurisdiction of the courts located in Jodhpur, Rajasthan, India.',
      },
      {
        heading: 'Changes to Terms',
        body:
          'Pizzapur reserves the right to update these Terms and Conditions at any time. Continued use of the Website after any changes constitutes your acceptance of the revised terms. We recommend reviewing this page periodically.',
      },
      {
        heading: 'Contact Us',
        body:
          'For any queries related to these Terms and Conditions, please contact us at: Pizzapur | Jodhpur, Rajasthan, India | Website: www.pizzapur.com | Email: contact@pizzapur.com',
      },
    ],
  },

  'privacy': {
    title: 'Privacy Policy',
    lastUpdated: 'June 2025',
    sections: [
      {
        heading: 'Introduction',
        body:
          'At Pizzapur, we are committed to protecting your privacy. This Privacy Policy explains what information we collect when you visit www.pizzapur.com, how we use it, and how we keep it safe. By using this Website, you agree to the practices described in this policy.',
      },
      {
        heading: 'Information We Collect',
        body: 'When you visit or interact with our Website, we may collect the following information:',
        bullets: [
          'Contact Information: Name, phone number, email address, and city — when you submit a franchise inquiry form or contact us voluntarily.',
          'Usage Data: IP address, browser type, device type, pages visited, and time spent — collected automatically through cookies and analytics tools.',
          'Communication Data: Any messages or information you send us via WhatsApp, email, or contact forms.',
        ],
      },
      {
        heading: 'How We Use Your Information',
        body: 'The information we collect is used for the following purposes:',
        bullets: [
          'To respond to your franchise inquiries and provide relevant information',
          'To contact you for follow-up regarding your inquiry',
          'To improve our Website and user experience',
          'To send promotional updates or offers (only if you have opted in)',
          'To comply with legal obligations',
        ],
      },
      {
        heading: 'Data Sharing',
        body:
          'Pizzapur does not sell, trade, or rent your personal information to any third parties. We may share your data only in the following circumstances:',
        bullets: [
          'With our internal team members for the purpose of responding to your inquiry',
          'With trusted service providers who assist us in operating the Website (e.g., hosting, analytics) — under strict confidentiality agreements',
          'When required by law or by a competent court or government authority',
        ],
      },
      {
        heading: 'Cookies',
        body:
          'Our Website may use cookies to enhance your browsing experience. Cookies are small text files stored on your device. We use cookies to:',
        bullets: [
          'Remember your preferences and settings',
          'Analyze Website traffic and usage patterns',
          'Improve Website performance',
        ],
        footer:
          'You may disable cookies through your browser settings. However, some features of the Website may not function properly if cookies are disabled.',
      },
      {
        heading: 'Data Retention',
        body:
          'We retain your personal information only for as long as necessary to fulfill the purposes outlined in this policy, or as required by applicable law. Once data is no longer needed, it is securely deleted.',
      },
      {
        heading: 'Data Security',
        body:
          'We take reasonable precautions to protect your personal information from unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the internet is 100% secure, and we cannot guarantee absolute security.',
      },
      {
        heading: 'Your Rights',
        body: 'You have the right to:',
        bullets: [
          'Request access to the personal data we hold about you',
          'Request correction of any inaccurate information',
          'Request deletion of your personal data (subject to legal obligations)',
          'Opt out of receiving promotional communications at any time',
        ],
        footer: 'To exercise any of these rights, please contact us at the details below.',
      },
      {
        heading: 'Third-Party Websites',
        body:
          'Our Website may contain links to external sites. Pizzapur is not responsible for the privacy practices of those websites. We encourage you to review the privacy policies of any third-party sites you visit.',
      },
      {
        heading: 'Changes to This Policy',
        body:
          'We may update this Privacy Policy from time to time. Any changes will be posted on this page with an updated date. Continued use of the Website after changes are posted constitutes your acceptance of the revised policy.',
      },
      {
        heading: 'Contact Us',
        body:
          'For any privacy-related questions or requests, please contact us at: Pizzapur | Jodhpur, Rajasthan, India | Website: www.pizzapur.com | Email: contact@pizzapur.com',
      },
    ],
  },

  'refund': {
    title: 'Refund Policy',
    lastUpdated: 'June 2025',
    sections: [
      {
        heading: 'Overview',
        body:
          'This Refund Policy applies to all payments made in connection with the Pizzapur franchise program. Please read this policy carefully before making any payment. By making a payment to Pizzapur, you agree to the terms outlined below.',
      },
      {
        heading: 'Token Amount — Non-Refundable',
        body:
          'The token amount of Rs. 21,000 paid at the time of location finalization is strictly non-refundable under any circumstances. This amount is collected as a location confirmation and reservation fee. Once a location is blocked in your name, the token amount will not be refunded regardless of the reason for cancellation. This applies even if the franchise application is later withdrawn by the applicant.',
      },
      {
        heading: 'Franchise Package Payment',
        body:
          'The franchise package payment (Rs. 3,50,000 + GST) is made in two installments after the token amount:',
        bullets: [
          '2nd Installment (50%): Paid before signing the franchise agreement — non-refundable once paid.',
          '3rd Installment (50%): Paid after signing the agreement and before dispatch from Jodhpur — non-refundable once dispatch is initiated.',
        ],
      },
      {
        heading: 'Supply Orders',
        body:
          'All supply orders (branded ingredients, packaging, and other materials) dispatched from Jodhpur require 100% advance payment before dispatch. Once an order is dispatched, it cannot be cancelled or refunded. In case of damaged or incorrect items received, the franchisee must report the issue within 48 hours of delivery with photographic evidence. Replacements for verified damaged/incorrect items will be processed at Pizzapur\'s discretion.',
      },
      {
        heading: 'Annual Renewal Fee',
        body:
          'The annual renewal fee of Rs. 20,000 is non-refundable once paid. If a franchisee chooses not to renew the agreement, the renewal fee already paid will not be refunded.',
      },
      {
        heading: 'Exceptional Circumstances',
        body:
          "Refund requests under exceptional circumstances (such as a significant failure on Pizzapur's part to deliver agreed services) will be reviewed on a case-by-case basis. Any such claims must be submitted in writing to Pizzapur within 7 days of the issue arising. Pizzapur's decision in such matters shall be final.",
      },
      {
        heading: 'Governing Law',
        body:
          'This Refund Policy shall be governed by the laws of India. Any disputes related to refunds shall be subject to the exclusive jurisdiction of the courts in Jodhpur, Rajasthan, India.',
      },
      {
        heading: 'Contact Us',
        body:
          'For any refund-related queries, please contact us at: Pizzapur | Jodhpur, Rajasthan, India | Website: www.pizzapur.com | Email: contact@pizzapur.com',
      },
    ],
  },
};
